import styled from "@emotion/styled";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography, useTheme } from "@mui/material";
import { ReactComponent as ArrowUpIcon } from "Components/_Shared/Icons/Assets/arrow-up.svg";
import { ReactComponent as InfoIcon } from "Components/_Shared/Icons/Assets/info.svg";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import type { PropsWithChildren } from "react";

type Props = {
  readonly title: string;
  readonly collapsible?: boolean;
  readonly defaultCollapsed?: boolean;
};

const StyledHint = styled(Box)`
  ${({ theme }) => `
    box-sizing: border-box;
    background-color: ${theme.colors.brandLight};
    padding: ${theme.spacing(Spacing.L)};
    border-radius: ${theme.spacing(Radius.L)};
    width: 100%;
  `}
`;

export const ModelingHint: React.FC<PropsWithChildren<Props>> = ({ title, children, collapsible = false, defaultCollapsed = false }) => {
  const theme = useTheme();

  const icon = <InfoIcon color={theme.palette.primary.main} />;
  const heading = (
    <Typography variant="body2" color="primary" fontWeight={500} lineHeight={1.4}>
      {title}
    </Typography>
  );
  const content = (
    <Typography component="div" variant="body2" lineHeight={1.3} mt={1}>
      {children}
    </Typography>
  );

  if (collapsible) {
    return (
      <Accordion
        defaultExpanded={!defaultCollapsed}
        disableGutters
        square
        elevation={0}
        sx={(theme) => ({
          p: Spacing.L,
          bgcolor: theme.colors.brandLight,
          borderRadius: theme.spacing(Radius.L),
          "&::before": { display: "none" },
        })}
      >
        <AccordionSummary
          expandIcon={<ArrowUpIcon style={{ width: 16, height: 16, transform: "rotate(180deg)" }} />}
          sx={{ minHeight: 0, p: 0, "& .MuiAccordionSummary-content": { m: 0 } }}
        >
          <Grid container width="100%">
            <Grid size={0.7}>{icon}</Grid>
            <Grid size={11.3}>{heading}</Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Grid container>
            <Grid size={11.3} offset={0.7}>
              {content}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <StyledHint>
      <Grid container justifyContent="space-between">
        <Grid size={0.7}>{icon}</Grid>
        <Grid size={11.3}>
          {heading}
          {content}
        </Grid>
      </Grid>
    </StyledHint>
  );
};
